import { useImperativeHandle, Ref, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import StatusFilter from '@/components/filters/StatusFilter/StatusFilter';
import PageSizeSelector from '@/components/PageSizeSelector/PageSizeSelector';
import FundFilter from '@/components/filters/FundFilter/FundFilter';
import styles from '@/components/filters/FiltersArea.module.scss';
import { RelationshipStatus } from '@/models/common/RelationshipStatus';
import { getStatusColors } from '@/helpers/cssHelpers';

export interface ChildStatusGroup {
  id: RelationshipStatus;
  iconColor: string;
}

// Get status filter groups with colors from CSS variables
const getChildStatusGroups = (): ChildStatusGroup[] => {
  const colors = getStatusColors();
  return [
    { id: RelationshipStatus.PendingApproval, iconColor: colors[RelationshipStatus.PendingApproval] },
    { id: RelationshipStatus.PendingCompletion, iconColor: colors[RelationshipStatus.PendingCompletion] },
    { id: RelationshipStatus.CompletionReturned, iconColor: colors[RelationshipStatus.CompletionReturned] },
    { id: RelationshipStatus.Approved, iconColor: colors[RelationshipStatus.Approved] },
    { id: RelationshipStatus.Rejected, iconColor: colors[RelationshipStatus.Rejected] },
  ];
};

const HIGHLIGHTED_STATUSES = new Set([RelationshipStatus.CompletionReturned]);

interface FiltersAreaProps {
  selectedFilters: string[];
  onFiltersChange: (filters: string[]) => void;
  pageSize: number;
  onPageSizeChange: (value: number) => void;
  selectedFundId: number | null;
  onFundChange: (fundId: number | null) => void;
  statusCounts?: Record<string, number>;
  ref?: Ref<{ resetFilters: () => void }>;
}

const FiltersArea = ({
  selectedFilters,
  onFiltersChange,
  pageSize,
  onPageSizeChange,
  selectedFundId,
  onFundChange,
  statusCounts,
  ref,
}: FiltersAreaProps) => {
  const { t } = useTranslation();

  // Memoize status groups with CSS variable colors
  const childStatusGroups = useMemo(() => getChildStatusGroups(), []);

  const resetAllFilters = () => {
    onFiltersChange([]);
  };

  const handleStatusGroupChange = (groupId: string) => {
    const isSelected = selectedFilters.includes(groupId);

    if (isSelected) {
      onFiltersChange(selectedFilters.filter((f) => f !== groupId));
    } else {
      onFiltersChange([...selectedFilters, groupId]);
    }
  };

  useImperativeHandle(ref, () => ({
    resetFilters: resetAllFilters,
  }));

  return (
    <div className={styles.filtersArea}>
      <div className={styles.filtersContainer}>
        {childStatusGroups.map((statusGroup) => {
          const hasItems = (statusCounts?.[statusGroup.id] ?? 0) > 0;
          const isHighlighted = hasItems && HIGHLIGHTED_STATUSES.has(statusGroup.id);
          return (
            <StatusFilter
              key={statusGroup.id}
              title={t(`childrenManagement.filters.${statusGroup.id}`)}
              isSelected={selectedFilters.includes(statusGroup.id)}
              onClick={() => handleStatusGroupChange(statusGroup.id)}
              iconColor={statusGroup.iconColor}
              isHighlighted={isHighlighted}
            />
          );
        })}
        <FundFilter selectedFundId={selectedFundId} onFundChange={onFundChange} />
      </div>
      <div className={styles.controlsContainer}>
        <PageSizeSelector value={pageSize} onChange={onPageSizeChange} />
      </div>
    </div>
  );
};

export default FiltersArea;

